/**
 * MovesList - Paginated moves list with filtering
 */
import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Swords, Shield, Sparkles } from 'lucide-react';
import { usePokemon } from '../../hooks/usePokemon';
import { useApp } from '../../context/AppContext';
import { TypeBadge } from './TypeBadge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const MOVES_PER_PAGE = 10;

// Move category icons
const CategoryIcon = ({ category }) => {
  switch (category) {
    case 'physical':
      return <Swords className="w-4 h-4 text-orange-500" />;
    case 'special':
      return <Sparkles className="w-4 h-4 text-indigo-500" />;
    case 'status':
      return <Shield className="w-4 h-4 text-gray-500" />;
    default:
      return null;
  }
};

function MoveRow({ move, learnMethod, level }) {
  const { getMove, getLocalizedName } = usePokemon();
  const [moveData, setMoveData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    let mounted = true;
    
    const fetchMove = async () => {
      const data = await getMove(move.move.name);
      if (mounted) {
        setMoveData(data);
        setLoading(false);
      }
    };
    
    fetchMove();
    return () => { mounted = false; };
  }, [move.move.name, getMove]);
  
  if (loading) {
    return (
      <tr className="border-b border-border">
        <td className="py-2 px-2"><Skeleton className="h-4 w-24" /></td>
        <td className="py-2 px-2"><Skeleton className="h-5 w-14 rounded-full" /></td>
        <td className="py-2 px-2"><Skeleton className="h-4 w-4" /></td>
        <td className="py-2 px-2 text-center"><Skeleton className="h-4 w-8 mx-auto" /></td>
        <td className="py-2 px-2 text-center"><Skeleton className="h-4 w-8 mx-auto" /></td>
        <td className="py-2 px-2 text-center"><Skeleton className="h-4 w-8 mx-auto" /></td>
      </tr>
    );
  }
  
  const localizedName = getLocalizedName(moveData?.names) || move.move.name;
  
  return (
    <motion.tr
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="border-b border-border hover:bg-muted/50 transition-colors"
    >
      <td className="py-2 px-2">
        <span className="font-medium capitalize text-sm text-foreground">
          {localizedName.replace(/-/g, ' ')}
        </span>
      </td>
      <td className="py-2 px-2">
        {moveData?.type?.name && (
          <TypeBadge type={moveData.type.name} size="xs" showLabel={false} />
        )}
      </td>
      <td className="py-2 px-2">
        <CategoryIcon category={moveData?.damage_class?.name} />
      </td>
      <td className="py-2 px-2 text-center text-sm text-muted-foreground">
        {moveData?.power || '—'}
      </td>
      <td className="py-2 px-2 text-center text-sm text-muted-foreground">
        {moveData?.accuracy || '—'}
      </td>
      <td className="py-2 px-2 text-center text-sm text-muted-foreground">
        {level || '—'}
      </td>
    </motion.tr>
  );
}

export function MovesList({ moves }) {
  const { t, language } = useApp();
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  
  // Process moves by learn method
  const processedMoves = useMemo(() => {
    if (!moves) return [];
    
    return moves.map(move => {
      const versionDetails = move.version_group_details || [];
      const latestDetail = versionDetails[versionDetails.length - 1] || {};
      
      return {
        ...move,
        learnMethod: latestDetail.move_learn_method?.name || 'unknown',
        level: latestDetail.level_learned_at || 0
      };
    });
  }, [moves]);
  
  // Filter moves
  const filteredMoves = useMemo(() => {
    if (filter === 'all') return processedMoves;
    
    const methodMap = {
      level: 'level-up',
      tm: 'machine',
      tutor: 'tutor',
      egg: 'egg'
    };
    
    return processedMoves.filter(m => m.learnMethod === methodMap[filter]);
  }, [processedMoves, filter]);
  
  // Sort by level for level-up moves
  const sortedMoves = useMemo(() => {
    return [...filteredMoves].sort((a, b) => {
      if (filter === 'level') return a.level - b.level;
      return a.move.name.localeCompare(b.move.name);
    });
  }, [filteredMoves, filter]);
  
  // Pagination
  const totalPages = Math.ceil(sortedMoves.length / MOVES_PER_PAGE);
  const paginatedMoves = sortedMoves.slice(
    (currentPage - 1) * MOVES_PER_PAGE,
    currentPage * MOVES_PER_PAGE
  );
  
  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);
  
  if (!moves || moves.length === 0) {
    return null;
  }
  
  return (
    <div className="space-y-3" data-testid="moves-list">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-bold text-foreground">{t('moves')}</h3>
        
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-32 h-8 text-sm" data-testid="moves-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('all') || 'Todos'}</SelectItem>
            <SelectItem value="level">{t('level')}</SelectItem>
            <SelectItem value="tm">{t('tm')}</SelectItem>
            <SelectItem value="tutor">{t('tutor')}</SelectItem>
            <SelectItem value="egg">{t('egg')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-left">
          <thead className="bg-muted/50">
            <tr>
              <th className="py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t('name')}
              </th>
              <th className="py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t('type') || 'Type'}
              </th>
              <th className="py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {language === 'es' ? 'Cat.' : 'Cat.'}
              </th>
              <th className="py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center">
                {t('power')}
              </th>
              <th className="py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center">
                {t('accuracy')}
              </th>
              <th className="py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center">
                {t('level') ? t('level').slice(0, 2) : 'Lvl'}
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedMoves.map((move, index) => (
              <MoveRow 
                key={`${move.move.name}-${index}`}
                move={move}
                learnMethod={move.learnMethod}
                level={move.level}
              />
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {sortedMoves.length} {t('moves').toLowerCase()}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              data-testid="moves-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              {currentPage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              data-testid="moves-next"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MovesList;
